/**
 * Deterministic font-size / letter-spacing step-down for {@link useFitLabel}.
 *
 * Pure (no React/DOM) so the step sequence is unit-testable in a node
 * environment. `useFitLabel` walks these steps largest-first and keeps the
 * first that fits on one line; if none fit it uses the last step (floor +
 * tighter tracking) and the caller wraps.
 *
 * The two floor steps re-try the floor size with progressively tighter
 * letter-spacing before giving up to a wrap: `tracking-tight` (-0.025em) then
 * `tracking-tighter` (-0.05em). The tighter step is the last resort for the
 * longest labels; most rows are single-line well before it.
 */

/** The smallest readable size the nav uses; a label never steps below this. */
export const FIT_LABEL_FLOOR_PX = 11;

/**
 * Inline `letter-spacing` a step is MEASURED and RENDERED with.
 * - `normal` is the empty string on purpose: it CLEARS the inline override so
 *   the element keeps its inherited/class spacing (e.g. `tracking-wide` on the
 *   uppercase drawer group headers). Forcing `"normal"` here made the measure
 *   drop that tracking and under-measure, so a header fit at the floor by
 *   measurement but clipped when rendered.
 * - `tight`/`tighter` are absolute values that override the inherited spacing,
 *   matching the `tracking-tight`/`tracking-tighter` classes FitLabel renders.
 */
export const FIT_LABEL_TRACKING_EM: Record<FitTracking, string> = {
  normal: "",
  tight: "-0.025em",
  tighter: "-0.05em"
};

export type FitTracking = "normal" | "tight" | "tighter";

/** One measurement candidate: a font-size and a letter-spacing level. */
export type FitStep = { px: number; tracking: FitTracking };

/**
 * Descending steps from `maxPx` (clamped up to the floor) down to the floor at
 * normal tracking, then the floor at `tight`, then the floor at `tighter`.
 * e.g. `stepsFrom(14)` → 14,13,12,11,11-tight,11-tighter.
 */
export const stepsFrom = (maxPx: number): FitStep[] => {
  const top = Math.max(Math.round(maxPx), FIT_LABEL_FLOOR_PX);
  const out: FitStep[] = [];
  for (let s = top; s >= FIT_LABEL_FLOOR_PX; s--) out.push({ px: s, tracking: "normal" });
  out.push({ px: FIT_LABEL_FLOOR_PX, tracking: "tight" });
  out.push({ px: FIT_LABEL_FLOOR_PX, tracking: "tighter" });
  return out;
};
